import { ElementType } from "react";
import { FaWhatsapp, FaServer } from "react-icons/fa";

// Define UserRole type (adjust based on your actual role definition)
export type UserRole = 'admin' | 'user' | 'member' | 'tester' | string; // Add other roles as needed

export type TriggerType = 'allowList' | 'blockList';

// Type definition for trigger sources
export type TriggerSource =
    | 'blueticks extension'        // WhatsApp extension
    | 'blueticks gateway'          // #184 A3: always-on Server Gateway (offline) inbound
    | 'blueticks assistant'        // Twilio with Blueticks number (personal assistant)
    | 'customer support'           // Twilio with customer's number (customer support - future)
    | 'instagram page'             // Instagram Business Account messages
    | 'facebook page';             // Facebook Page messages

// Stable camelCase keys used for i18n lookups and triggerSourceData storage.
export const TRIGGER_SOURCE_KEY_MAP: Record<TriggerSource, string> = {
    'blueticks extension': 'blueticksExtension',
    'blueticks gateway': 'blueticksGateway',
    'blueticks assistant': 'blueticksAssistant',
    'customer support': 'customerSupport',
    'instagram page': 'instagramPage',
    'facebook page': 'facebookPage',
};

// Source-specific data interfaces
export interface InstagramTriggerSourceData {
  selectedPageId?: string;
  selectedPageName?: string;
  selectedPageUsername?: string;
  selectedPageProfilePicture?: string;
  messagingEnabled?: boolean;
  facebookPageId?: string;
}

export interface FacebookTriggerSourceData {
  selectedPageId?: string;
  selectedPageName?: string;
  selectedPageProfilePicture?: string;
}

// Union type of all possible source data
export type TriggerSourceData = 
  | InstagramTriggerSourceData
  | FacebookTriggerSourceData
  | undefined;

// Interface for trigger source properties
export interface TriggerSourceDefinition {
    label: string;
    value: TriggerSource;
    description: string;
    icon: ElementType; // Use ElementType for React components
    enabled: boolean; // whether this option is currently enabled in the UI
    requiresConfiguration?: boolean; // whether this source requires additional configuration
    configurationType?: 'instagram_page_selection' | 'facebook_page_selection';
    adminOnly?: boolean; // New flag to mark admin-only sources
    requiresGateway?: boolean; // #184 A3: only offer when an active Server Gateway is connected
}

// Centralized trigger sources definitions for use across components
export const ALL_TRIGGER_SOURCES: TriggerSourceDefinition[] = [ // Renamed to ALL_TRIGGER_SOURCES
    {
        label: 'WhatsApp Extension',
        value: 'blueticks extension',
        description: 'Messages received through the WhatsApp Web extension',
        icon: FaWhatsapp,
        enabled: true,
        adminOnly: false, // Default users can use this
    },
    {
        // #184 A3: the always-on Server Gateway (offline mode) captures inbound server-side, so an
        // account whose active engine is a gateway can trigger the agent without a live browser tab.
        // Only surfaced when a gateway is actually connected (see getAvailableTriggerSources).
        label: 'WhatsApp Gateway',
        value: 'blueticks gateway',
        description: 'Messages received through your always-on Server Gateway (works offline, no browser tab needed)',
        icon: FaServer,
        enabled: true,
        adminOnly: false,
        requiresGateway: true,
    },
    // {
    //     label: 'Instagram Messages',
    //     value: 'instagram page',
    //     description: 'Messages received from your Instagram business account',
    //     icon: Instagram,
    //     enabled: true, // Keep enabled globally, filter by role
    //     requiresConfiguration: true,
    //     configurationType: 'instagram_page_selection',
    //     adminOnly: true, // Mark as admin-only
    // },
    // { 
    //     label: 'Facebook Messages',
    //     value: 'facebook page',
    //     description: 'Messages received from your Facebook page inbox',
    //     icon: Facebook,
    //     enabled: true, // Keep enabled globally, filter by role
    //     requiresConfiguration: true,
    //     configurationType: 'facebook_page_selection',
    //     adminOnly: true, // Mark as admin-only
    // },
    // { 
    //     label: 'Blueticks Assistant', 
    //     value: 'blueticks assistant',
    //     description: 'Messages sent to the official Blueticks phone number via Twilio',
    //     icon: WebhookOutlined,
    //     enabled: true
    // },
    // { 
    //     label: 'Customer Support', 
    //     value: 'customer support',
    //     description: "Messages sent to the customer's own Twilio phone number",
    //     icon: SupportAgent,
    //     enabled: false // currently disabled until the feature is ready
    // },
];

/**
 * Filters available trigger sources based on user role.
 * @param userRole The role of the current user.
 * @returns An array of TriggerSourceDefinition available for the user.
 */
export const getAvailableTriggerSources = (
    userRole: UserRole,
    opts?: { hasGateway?: boolean },
): TriggerSourceDefinition[] => {
    return ALL_TRIGGER_SOURCES.filter(source => {
        // Source must be globally enabled
        if (!source.enabled) {
            return false;
        }
        // If source is admin-only, user must be an admin or tester
        if (source.adminOnly && !['admin', 'tester'].includes(userRole)) {
            return false;
        }
        // #184 A3: gateway-only sources appear only when an active Server Gateway is connected.
        if (source.requiresGateway && !opts?.hasGateway) {
            return false;
        }
        // Otherwise, the source is available
        return true;
    });
};

export interface TriggerData {
    type: 'trigger';
    triggerType: TriggerType;
    contacts: string[]; // Array of contact IDs
    allowedTriggerSources?: TriggerSource[]; // Array of allowed trigger sources
    
    /**
     * Source-specific configuration data.
     * IMPORTANT: Standardized naming convention for all trigger sources:
     * - All trigger source keys are stored using camelCase format
     * - For example: 
     *   - 'instagram page' → 'instagramPage'
     *   - 'blueticks extension' → 'blueticksExtension'
     *   - 'customer support' → 'customerSupport'
     * 
     * This standardization ensures consistent access across front-end and back-end.
     * The system handles backward compatibility by checking multiple key formats.
     */
    triggerSourceData?: Partial<Record<string, TriggerSourceData>>;
}

export type TriggerScope = {
    type: 'ALL_MESSAGES' | 'SOME_MESSAGES' | 'NO_MESSAGES';
    triggerData?: TriggerData;
}; 